import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { TwSolicitudCamViewModel } from '../../../types/solicitudcam-datosbasicos.types';
import { SolicitudCamService } from '../solicitud-cam.service';
import { catchError, of, tap } from 'rxjs';
import { ForeignKeyValidator } from 'src/app/components/core/_infrastructure/classes/foreignKeyValidator';
import { EnumSiNoTodos, Tc } from 'src/app/components/core/_infrastructure/types/common';
import { CampaniasGlobalService } from '../../../services/global-campanias.service';
import { NavMenuService } from 'src/app/components/core/components/navmenu/navmenu.service';
import { PeriodoTipoSolicitudViewModel, PeriodoDatosCaCamViewModel, CASelectedViewModel } from '../../../buscadores/solicitudes/buscador-solicitud-cam.types';
import { EnumCodigosTwTcAcciones } from 'src/app/components/_importacion/solicitudes/types/enum';
import { VariedadService } from 'src/app/components/_mantenimiento/especies/variedades/variedad.service';
import { VariedadItemResultsViewModel } from 'src/app/components/_mantenimiento/especies/variedades/variedad.types';
import { UsuariosService } from 'src/app/components/_mantenimiento/usuarios/usuarios.service';
import { UsuarioViewModel } from 'src/app/components/_mantenimiento/usuarios/usuarios.types';
import { ConfirmationService } from 'primeng/api';
import { PeriodoCamService } from 'src/app/components/_mantenimiento/campanias/periodos/datosbasicos/periodocam.service';

@Component({
    selector: 'app-solicitud-cam-wrapper',
    templateUrl: './solicitud-cam-wrapper.component.html',
    styleUrls: ['./solicitud-cam-wrapper.component.scss'],
})
export class SolicitudCamWrapperComponent extends ComponentBase implements OnInit {
    solicitudCam: TwSolicitudCamViewModel;
    formGroup: FormGroup;
    campanias: Tc[];
    periodos: Tc[];
    periodoPlazoPresentacionVigente = true;
    tiposSolicitud: PeriodoTipoSolicitudViewModel[];
    CAs: PeriodoDatosCaCamViewModel[];
    EAs: Tc[];
    variedades: VariedadItemResultsViewModel;
    visibilitySpinnerSave = false;
    visibilitySpinnerAnular = false;
    usuarioExterno: UsuarioViewModel;

    private successMessage = 'Solicitud de Campaña guardada correctamente';
    private successMsgAnulacion = 'Solicitud de Campaña anulada correctamente';
    private listUrl = '/campanias/solicitudes';
    private editionUrl = 'campanias/solicitud/datosbasicos/detail/';

    private activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const id: any = params.get('id');
        if (id) {
            this.getSolicitudCam(id);
        } else {
            this.resetMenuSolicitudCam();
            this.getCampanias();
            this.getDatosOperador();
        }
    });

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private solicitudCamService: SolicitudCamService,
        private globalCampaniasService: CampaniasGlobalService,
        private periodoCamService: PeriodoCamService,
        private navMenuService: NavMenuService,
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        private variedadService: VariedadService,
        private usuarioService: UsuariosService,
        private confirmationService: ConfirmationService,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.preparePresentation();
    }

    private preparePresentation(): void {
        this.buildForm();
    }

    private resetMenuSolicitudCam(): void {
        this.navMenuService.clearMenuServiceForSolicitudCam();
    }

    private getCampanias(): void {
        this.globalCampaniasService.getCampaniasByProfile(this.activos).subscribe(res => {
            this.campanias = res;
        });
    }

    onCampaniaSelected(idCampania?: number): void {
        this.getPeriodos(idCampania);
        // this.onPeriodoSelected(this.solicitudCam?.IdPeriodoCam);
    }

    private getPeriodos(idCampania?: number): void {
        if (!idCampania) {
            this.periodos = [];
            return;
        }
        this.globalCampaniasService.getPeriodosByCampania(idCampania, this.activos).subscribe(res => {
            this.periodos = res;
        });
    }

    /**
     * Cuando se selecciona un periodo nuevo, se vuelven a cargar las CAs y los tipos de solicitud.
     * @param idPeriodo number
     * @returns void
     */
    onPeriodoSelected(idPeriodo?: number): void {
        if (!idPeriodo) {
            this.CAs = [];
            this.EAs = [];
            this.tiposSolicitud = [];
            return;
        }
        this.periodoCamService.getPeriodoEnPlazoPresentacionVigente(idPeriodo).subscribe(res => {
            this.periodoPlazoPresentacionVigente = res;
        });
        this.globalCampaniasService.getCAsByPeriodo(idPeriodo).subscribe(res => {
            this.CAs = res;
        });
        this.globalCampaniasService.getTiposSolicitudCam(idPeriodo).subscribe(res => {
            this.tiposSolicitud = res;
        });
        this.getVariedades(idPeriodo);
    }

    /**
     * Cuando se selecciona una CA, se vuelven a cargar las EAs relacionadas con ésa CA.
     * @param param$ CASelectedViewModel
     * @returns void
     */
    onCASelected(param$: CASelectedViewModel): void {
        if (!param$.idPeriodo || !param$.idCA) {
            this.EAs = [];
            return;
        }
        this.globalCampaniasService
            .getEAsByCA(param$)
            .pipe(
                tap(res => {
                    this.EAs = res;
                }),
                catchError(error => {
                    this.EAs = [];
                    this.catchServerError(error);
                    this.formGroup.disable();
                    if (error?.error?.length > 0) {
                        this.addErrorMessage(error.error[0]?.message);
                    }
                    return of(null);
                }),
            )
            .subscribe();
    }

    private buildForm(): void {
        this.formGroup = this.fb.group({
            Id: [null], // Not visible but needed
            IdCampania: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            IdPeriodoCam: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            IdCA: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            IdEntidadAuditora: [null],
            IdTipoSolicitudCam: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])], // Tipo de recinto
            IdEstadoSolicitudCam: [null], // Not visible but needed
            EstadoSolicitudCam: [{ value: null, disabled: true }], // readonly
            FechaCreacion: [null], // Not visible but needed
            FechaSolicitud: [{ value: null, disabled: true }], // readonly
            Codigo: [{ value: null, disabled: true }], // readonly
            IdUsuario: [false], // Not visible but needed,
            IdPeriodoPlazoCam: [null], // Not visible but needed,
            IdAlmacenExportacionCicloCorto: [null], // Not visible but needed,
            OperadorRazonSocial: [{ value: null, disabled: true }], // readonly
            OperadorNif: [{ value: null, disabled: true }], // readonly
            OperadorTelefono: [{ value: null, disabled: true }], // readonly
        });
        this.form = this.formGroup;
    }

    /**
     * Se cargan los datos de la solicitud y los desplegables en función de los datos de ésta.
     * @param idSolicitudCam number
     */
    private getSolicitudCam(idSolicitudCam: number): void {
        this.solicitudCamService
            .getSolicitudCam(idSolicitudCam)
            .pipe(
                tap((v: TwSolicitudCamViewModel) => {
                    this.solicitudCam = v;
                    this.getCampanias();
                    this.loadDependentOnCampania();
                    this.navMenuService.updateMenuServiceForSolicitudCam(v.Id!, v.IdEstadoSolicitudCam!, v.IdTipoSolicitudCam);
                    this.getVariedades(this.solicitudCam?.IdPeriodoCam);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.formGroup.disable();
                    return of([]);
                }),
            )
            .subscribe();
    }

    /**
     * Solo obtenemos los datos del usuario externo cuando estamos creando la solicitud.
     */
    private getDatosOperador(): void {
        if (this.loggedInUser && !this.solicitudCam) {
            this.usuarioService
                .getUsuarioExterno(this.loggedInUser.Id)
                .pipe(
                    tap((v: UsuarioViewModel) => {
                        this.usuarioExterno = v;
                    }),
                    catchError(error => {
                        this.catchServerError(error);
                        this.formGroup.disable();
                        return of(null);
                    }),
                )
                .subscribe();
        }
    }

    private getVariedades(idPeriodoCam: number): void {
        this.variedadService
            .getVariedadesByPeriodo(idPeriodoCam)
            .pipe(
                tap((v: VariedadItemResultsViewModel) => {
                    this.variedades = v;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.formGroup.disable();
                    return of([]);
                }),
            )
            .subscribe();
    }

    /**
     * Este método se ejecuta cuando cambia la campaña seleccionada.
     * Se cargan los periodos de la campaña indicada en la solicitud.
     * Además, se cargan los datos dependientes del periodo (indicado en la solicitud).
     */
    private loadDependentOnCampania(): void {
        this.onCampaniaSelected(this.solicitudCam?.IdCampania);
        this.onPeriodoSelected(this.solicitudCam?.IdPeriodoCam);
        const caSelected = new CASelectedViewModel();
        caSelected.idCA = this.solicitudCam?.IdCa;
        caSelected.idPeriodo = this.solicitudCam?.IdPeriodoCam;
        this.onCASelected(caSelected);
    }

    /**
     * Se envían los datos de la solicitud al core para guardarlos en la tabla.
     */
    onSave(): void {
        const model = { ...this.formGroup.value };
        this.submitted = true;
        this.visibilitySpinnerSave = false;
        this.solicitudCamService
            .saveSolicitudCam(model)
            .pipe(
                tap(res => {
                    this.visibilitySpinnerSave = false;
                    this.addSuccessMessage(this.successMessage);

                    if (!model.Id) {
                        this.gotoEdition(res);
                        return;
                    }
                    this.getSolicitudCam(model.Id);
                }),
                catchError(error => {
                    this.visibilitySpinnerSave = false;
                    this.catchServerError(error);
                    if (error?.error?.length > 0) {
                        this.addErrorMessage(error.error[0]?.message);
                    }
                    return of(null);
                }),
            )
            .subscribe();
    }

    onAnular(): void {
        this.confirmationService.confirm({
            message: '¿Está seguro de anular la solicitud actual?.\nTenga en cuenta que no podrá deshacer la operación.',
            accept: () => {
                this.visibilitySpinnerAnular = true;
                this.solicitudCamService
                    .anularSolicitudCam(this.solicitudCam.Id!)
                    .pipe(
                        tap(() => {
                            this.visibilitySpinnerAnular = false;
                            this.addSuccessMessage(this.successMsgAnulacion);
                            this.getSolicitudCam(this.solicitudCam.Id!);
                        }),
                        catchError(error => {
                            this.visibilitySpinnerAnular = false;
                            this.catchServerError(error);
                            if (error?.error?.length > 0) {
                                this.addErrorMessage(error.error[0]?.message);
                            }
                            return of(null);
                        }),
                    )
                    .subscribe();
            },
        });
    }

    onBack(): void {
        this.router.navigate([this.listUrl]);
    }

    private gotoEdition(IdSolicitud: number): void {
        this.router.navigate([this.editionUrl, IdSolicitud]);
    }

    private get activos(): EnumSiNoTodos {
        if (!this.solicitudCam) {
            // Cuando no se ha creado la solicitud, necesitamos solamente las campañas activas.
            return EnumSiNoTodos.SI;
        }
        // Cuando la solicitud ya existe, necesitamos todas las campañas. Si se entra a la solicitud, cuando un periodo-campaña no está vigente, también necesitamos los datos.
        return EnumSiNoTodos.TODOS;
    }

    private get formInvalid(): boolean {
        return !this.formGroup.valid || !this.formGroup.dirty || this.formGroup.pristine;
    }

    private get userIsOwner(): boolean {
        return this.solicitudCam && this.loggedInUser?.Id === this.solicitudCam?.IdUsuario;
    }

    private get adminHasWriteAccess(): boolean {
        return this.isAdmin && (this.solicitudCam !== null || this.solicitudCam !== undefined);
    }

    /**
     * Solo el usuario Operador de Campaña podrá crear una solicitud
     * @return boolean
     */
    private get hasCreationAccess(): boolean {
        const hasAccess = this.loggedInUser?.getAction(EnumCodigosTwTcAcciones.NEW_SOL_CAM).Editable || false;
        return hasAccess && !this.solicitudCam;
    }

    /**
     * Este método indica si se puede editar la solicitud.
     * Si el objeto solicitudCam no se ha cargado, no se le habilita el permiso.
     * No solo se comprueba que el usuario (por perfil) tenga los permisos necesarios, sino que sea o autor de la misma o admin.
     * @return boolean
     */
    private get hasWriteAccess(): boolean {
        if (!this.solicitudCam) return false;
        const hasAccess =
            this.loggedInUser?.getAction(EnumCodigosTwTcAcciones.SAVE_DATOS_BASICOS_SOL_CAM, this.solicitudCam?.IdEstadoSolicitudCam).Editable || false;
        return hasAccess && (this.userIsOwner || this.adminHasWriteAccess);
    }

    get disableSave(): boolean {
        return this.formInvalid || this.readonly;
    }

    get hasAnularAccess(): boolean {
        return this.loggedInUser?.getAction(EnumCodigosTwTcAcciones.ANULAR_SOLICITUD_CAMPANIA, this.solicitudCam?.IdEstadoSolicitudCam).Visible || false;
    }

    get readonly(): boolean {
        return (!this.hasWriteAccess && !this.hasCreationAccess) || (!this.esPeriodoVigente && !this.plazoPresentacionVigente);
    }

    get esPeriodoVigente(): boolean {
        return this.solicitudCam?.PeriodoVigente;
    }

    get plazoPresentacionVigente(): boolean {
        if (!this.formGroup) {
            return true;
        }
        if (!this.solicitudCam && this.periodos) {
            return this.periodos?.length > 0 && this.periodoPlazoPresentacionVigente;
        }
        if (!this.periodos || this.periodos?.length === 0) {
            return true;
        }
        return this.solicitudCam.PeriodoPresentacionVigente && this.periodos.length > 0 && this.periodoPlazoPresentacionVigente;
    }
}
